import type { EntityId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';
import type { ControlCategory } from './problem.js';

/**
 * Direction — the top of the growth spine. This is what gives Targets, Projects,
 * Routines, and Tasks their meaning:
 *
 *   Vision → GOAL → MILESTONE → Target → Systems/Routines/Habits/Tasks → Evidence → Review
 *
 * Per the life-graph model, a Milestone is NOT a private child of one Goal — it
 * is its own node that can contribute_to MANY goals (e.g. "Learn Networking"
 * advances security, cloud, and DevOps goals at once). Goals carry the pillar
 * they grow and the WHY behind them, so daily action stays connected to purpose
 * without becoming pressure. No life score, no comparison, no guilt.
 */

/** The eight holistic pillars (organizing lenses, never top-level clutter). */
export type Pillar =
  | 'physical'
  | 'mental'
  | 'spiritual'
  | 'intellectual'
  | 'financial'
  | 'professional'
  | 'mission'
  | 'presence';

export const PILLARS: readonly Pillar[] = [
  'physical',
  'mental',
  'spiritual',
  'intellectual',
  'financial',
  'professional',
  'mission',
  'presence',
] as const;

export const PILLAR_LABEL: Record<Pillar, string> = {
  physical: 'Physical',
  mental: 'Mental',
  spiritual: 'Spiritual / Values',
  intellectual: 'Intellectual / Knowledge',
  financial: 'Financial',
  professional: 'Professional / Social',
  mission: 'Mission / Work',
  presence: 'Presence / Influence',
};

export type GoalHorizon = 'lifetime' | 'decade' | 'year' | 'quarter' | 'month';
export const GOAL_HORIZONS: readonly GoalHorizon[] = ['lifetime', 'decade', 'year', 'quarter', 'month'] as const;

export type GoalStatus = 'active' | 'achieved' | 'paused' | 'dropped';
export const GOAL_STATUSES: readonly GoalStatus[] = ['active', 'achieved', 'paused', 'dropped'] as const;

export interface Goal extends RecordEnvelope {
  readonly kind: 'goal';
  readonly title: string;
  readonly pillar: Pillar;
  readonly horizon: GoalHorizon;
  /** The meaning behind the goal — preserved without turning into pressure. */
  readonly why: string;
  readonly status: GoalStatus;
  /** Linked projects/targets/skills (ids only, no duplication). */
  readonly linkedIds: readonly EntityId[];
}

export interface GoalInput {
  readonly title: string;
  readonly pillar?: Pillar;
  readonly horizon?: GoalHorizon;
  readonly why?: string;
  readonly privacyClass?: PrivacyClass;
}

export interface GoalPatch {
  readonly title?: string;
  readonly pillar?: Pillar;
  readonly horizon?: GoalHorizon;
  readonly why?: string;
}

export type MilestoneStatus = 'open' | 'in_progress' | 'done';
export const MILESTONE_STATUSES: readonly MilestoneStatus[] = ['open', 'in_progress', 'done'] as const;

export interface Milestone extends RecordEnvelope {
  readonly kind: 'milestone';
  readonly title: string;
  readonly status: MilestoneStatus;
  /** Goals this milestone advances. A milestone can serve MANY goals. */
  readonly contributesToGoalIds: readonly EntityId[];
  /** Targets / projects realising this milestone (ids only). */
  readonly linkedIds: readonly EntityId[];
}

export interface MilestoneInput {
  readonly title: string;
  readonly contributesToGoalIds?: readonly EntityId[];
  readonly privacyClass?: PrivacyClass;
}

export interface GoalProgress {
  readonly goalId: EntityId;
  readonly totalMilestones: number;
  readonly doneMilestones: number;
  /** 0..1 fraction of contributing milestones done; 0 when none exist yet. */
  readonly fraction: number;
}

/**
 * Progress of a goal = done / total over the milestones that contribute to it.
 * Pure. A goal with no milestones reads 0 (needs decomposition), never "failing".
 */
export function goalProgress(
  goalId: EntityId,
  milestones: readonly Pick<Milestone, 'status' | 'contributesToGoalIds'>[],
): GoalProgress {
  let total = 0;
  let done = 0;
  for (const m of milestones) {
    if (!m.contributesToGoalIds.some((id) => String(id) === String(goalId))) continue;
    total += 1;
    if (m.status === 'done') done += 1;
  }
  return {
    goalId,
    totalMilestones: total,
    doneMilestones: done,
    fraction: total === 0 ? 0 : done / total,
  };
}

export interface DirectionSummary {
  readonly totalGoals: number;
  readonly activeGoals: number;
  readonly achievedGoals: number;
  readonly byPillar: Record<Pillar, number>;
  /** Pillars with no active goal — gentle "neglected area" hint, not a scold. */
  readonly neglectedPillars: readonly Pillar[];
}

/** Pure, neutral roll-up for the Direction header. Highlights breadth, not rank. */
export function summarizeDirection(goals: readonly Pick<Goal, 'status' | 'pillar'>[]): DirectionSummary {
  const byPillar: Record<Pillar, number> = {
    physical: 0,
    mental: 0,
    spiritual: 0,
    intellectual: 0,
    financial: 0,
    professional: 0,
    mission: 0,
    presence: 0,
  };
  const activePillars = new Set<Pillar>();
  let active = 0;
  let achieved = 0;
  for (const g of goals) {
    byPillar[g.pillar] += 1;
    if (g.status === 'active') {
      active += 1;
      activePillars.add(g.pillar);
    } else if (g.status === 'achieved') {
      achieved += 1;
    }
  }
  const neglectedPillars = PILLARS.filter((p) => !activePillars.has(p));
  return { totalGoals: goals.length, activeGoals: active, achievedGoals: achieved, byPillar, neglectedPillars };
}

/**
 * Direction sub-lenses — the qualitative side of Direction, distinct from the
 * Goal/Milestone spine. One DirectionItem entity backs three calm lenses:
 *   - roles:      who I'm being (son, founder, learner…) and its care/duties
 *   - values:     values, beliefs, and mindsets that guide choices
 *   - challenges: active challenges and opportunities, placed on the control
 *                 spectrum so effort goes where it can actually matter
 *
 * No scoring, no pressure labels — these hold meaning, not metrics.
 */
export type DirectionLens = 'roles' | 'values' | 'challenges';
export const DIRECTION_LENSES: readonly DirectionLens[] = ['roles', 'values', 'challenges'] as const;

export type DirectionItemKind = 'role' | 'value' | 'mindset' | 'challenge' | 'opportunity';
export const DIRECTION_ITEM_KINDS: readonly DirectionItemKind[] = ['role', 'value', 'mindset', 'challenge', 'opportunity'] as const;

/** Suggested item kinds per lens (UI hint; the model accepts any kind). */
export const LENS_KINDS: Record<DirectionLens, readonly DirectionItemKind[]> = {
  roles: ['role'],
  values: ['value', 'mindset'],
  challenges: ['challenge', 'opportunity'],
};

export interface DirectionItem extends RecordEnvelope {
  readonly kind: 'direction_item';
  readonly lens: DirectionLens;
  readonly itemKind: DirectionItemKind;
  readonly title: string;
  readonly detail: string;
  /** Agency posture (control/influence/accept) — meaningful for challenges; null otherwise. */
  readonly posture: ControlCategory | null;
  readonly linkedIds: readonly EntityId[];
}

export interface DirectionItemInput {
  readonly lens: DirectionLens;
  readonly itemKind?: DirectionItemKind;
  readonly title: string;
  readonly detail?: string;
  readonly posture?: ControlCategory | null;
  readonly privacyClass?: PrivacyClass;
}

/** Default item kind for a lens when none is supplied. */
export function defaultItemKind(lens: DirectionLens): DirectionItemKind {
  return LENS_KINDS[lens][0]!;
}
